import { useState, useCallback, useEffect } from 'react';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Cell } from '@/components/Cell';
import { Empty } from '@/components/Empty';
import { Spinner } from '@/components/Spinner';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { useSupabaseQuery, sb } from '@/hooks/useSupabaseQuery';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';
import { fmtDateTime, fmtPct, fmtUsd } from '@/lib/format';
import type { AlphaAlert, BetaAlert, EquitySnapshot, UserSettings } from '@/types/domain';

/** Снимок баланса старше этого порога считается устаревшим (опрос раз в 10 секунд). */
const STALE_BALANCE_MS = 5 * 60 * 1000;

/**
 * Раздел Alpha. Показывает текущий баланс Account equity и
 * историю срабатываний правила «изменение > 100% за 20 проверок».
 */
export function AlphaPage(): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();
  const settings = useSupabaseQuery<UserSettings>(
    async () => {
      const result = await sb
        .from('user_settings')
        .select('gate_api_key, gate_api_secret, gate_api_last_error, gate_api_last_ok_at, alpha_auto_sell_enabled, alpha_threshold_pct, alpha_extreme_pct')
        .maybeSingle();
      return { data: result.data as UserSettings | null, error: result.error };
    },
    [],
    15_000,
  );
  const last = useSupabaseQuery<EquitySnapshot[]>(
    () => sb.from('equity_snapshots').select('*').order('taken_at', { ascending: false }).limit(1),
    [],
    10_000,
  );
  const alerts = useSupabaseQuery<AlphaAlert[]>(
    () => sb.from('alpha_alerts').select('*').order('created_at', { ascending: false }).limit(50),
    [],
    10_000,
  );
  const betaAlerts = useSupabaseQuery<BetaAlert[]>(
    () => sb.from('beta_alerts').select('*').order('new_hour', { ascending: false }).limit(100),
    [],
    30_000,
  );

  const current = last.data?.[0];
  const s = settings.data as UserSettings | null;
  const hasKeys = !!(s?.gate_api_key && s?.gate_api_secret);
  const gateError = s?.gate_api_last_error ?? null;

  const [autoSellSaving, setAutoSellSaving] = useState(false);
  const [autoSellOptimistic, setAutoSellOptimistic] = useState<boolean | null>(null);
  const [autoSellError, setAutoSellError] = useState<string | null>(null);

  const [thresholdPct, setThresholdPct] = useState('100');
  const [extremePct, setExtremePct] = useState('1000');
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [thresholdError, setThresholdError] = useState<string | null>(null);

  // Sync threshold state when settings load
  useEffect(() => {
    if (s) {
      setThresholdPct(String(s.alpha_threshold_pct ?? 100));
      setExtremePct(String(s.alpha_extreme_pct ?? 1000));
    }
  }, [s]);

  // Снимаем оптимистичное значение, когда сервер вернул то же состояние.
  useEffect(() => {
    if (autoSellOptimistic !== null && !autoSellSaving && s?.alpha_auto_sell_enabled === autoSellOptimistic) {
      setAutoSellOptimistic(null);
    }
  }, [s, autoSellOptimistic, autoSellSaving]);

  const handleAutoSellToggle = useCallback(async () => {
    if (!userId) {
      setAutoSellError('Не авторизован. Откройте приложение через Telegram.');
      notify('error');
      return;
    }
    const prevValue = autoSellOptimistic ?? s?.alpha_auto_sell_enabled ?? false;
    const newValue = !prevValue;
    setAutoSellOptimistic(newValue);
    setAutoSellError(null);
    setAutoSellSaving(true);
    const { error } = await sb.from('user_settings').upsert({
      user_id: userId,
      alpha_auto_sell_enabled: newValue,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });
    setAutoSellSaving(false);
    if (error) {
      setAutoSellOptimistic(prevValue);
      setAutoSellError(error.message);
      notify('error');
      return;
    }
    notify('success');
    settings.refetch();
  }, [userId, s, autoSellOptimistic, settings, notify]);

  const handleThresholdSave = useCallback(async () => {
    if (!userId) {
      setThresholdError('Не авторизован. Откройте приложение через Telegram.');
      notify('error');
      return;
    }
    const thr = Number(thresholdPct);
    const ext = Number(extremePct);
    if (!Number.isFinite(thr) || thr <= 0 || thr > 10000) {
      setThresholdError('Порог Alpha должен быть от 1 до 10000');
      notify('error');
      return;
    }
    if (!Number.isFinite(ext) || ext <= thr || ext > 100000) {
      setThresholdError('Порог Extreme должен быть > порога Alpha и ≤ 100000');
      notify('error');
      return;
    }
    setThresholdError(null);
    setThresholdSaving(true);
    const { error } = await sb.from('user_settings').upsert({
      user_id: userId,
      alpha_threshold_pct: thr,
      alpha_extreme_pct: ext,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });
    setThresholdSaving(false);
    if (error) {
      setThresholdError(`Не удалось сохранить: ${error.message}`);
      notify('error');
      return;
    }
    settings.refetch();
    notify('success');
  }, [userId, thresholdPct, extremePct, settings, notify]);

  /** Определяем подходящую заглушку для секции баланса. */
  function renderBalanceEmpty(): JSX.Element {
    if (settings.error) {
      return <Empty icon="⚠️" title="Не удалось загрузить настройки" hint={settings.error} />;
    }
    if (last.error) {
      return <Empty icon="⚠️" title="Не удалось загрузить баланс" hint={last.error} />;
    }
    if (!hasKeys) {
      return <Empty icon="🔑" title="Нет данных" hint="Заполните Gate API ключи в настройках." />;
    }
    if (gateError) {
      return (
        <Empty
          icon="⚠️"
          title="Ошибка Gate API"
          hint={humanizeGateError(gateError)}
        />
      );
    }
    return <Empty icon="⏳" title="Ожидание данных" hint="Первый опрос произойдёт в течение минуты." />;
  }

  const autoSellEnabled = autoSellOptimistic ?? s?.alpha_auto_sell_enabled ?? false;
  const snapshotAgeMs = current ? Date.now() - new Date(current.taken_at).getTime() : 0;
  const isStale = !!current && snapshotAgeMs > STALE_BALANCE_MS;

  return (
    <Page title="Alpha — баланс">
      <Section header="Текущий баланс" footer="Опрашивается каждые 10 секунд из Gate API.">
        {last.isLoading || settings.isLoading ? (
          <Spinner />
        ) : current ? (
          <>
            <Cell title={fmtUsd(current.equity)} subtitle={fmtDateTime(current.taken_at)} />
            {gateError ? (
              <Cell before="⚠️" title="Ошибка Gate API" subtitle={humanizeGateError(gateError)} />
            ) : isStale ? (
              <Cell
                before="⏳"
                title="Данные устарели"
                subtitle={`Последний снимок ${Math.round(snapshotAgeMs / 60_000)} мин назад. Проверьте, что фоновый опрос poller-equity (cron) запущен.`}
              />
            ) : null}
          </>
        ) : (
          renderBalanceEmpty()
        )}
      </Section>

      <Section
        header="Авто-продажа при EXTREME"
        footer="При срабатывании EXTREME автоматически выставляются лимитные заявки на продажу позиций, у которых bid ≥ 5× средней цены входа. Заявка отменяется автоматически через 5 минут, если не исполнена."
      >
        {settings.isLoading ? (
          <Spinner />
        ) : (
          <Cell
            title={autoSellEnabled ? '✅ Авто-продажа включена' : '⬜ Авто-продажа выключена'}
            subtitle={autoSellEnabled ? 'Нажмите, чтобы выключить' : 'Нажмите, чтобы включить'}
            onClick={autoSellSaving ? undefined : handleAutoSellToggle}
            after={autoSellSaving ? <Spinner /> : null}
          />
        )}
        {autoSellError && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', fontSize: 'var(--font-sm)', color: 'var(--tg-destructive)' }}>
            Ошибка: {autoSellError}
          </div>
        )}
      </Section>

      <Section
        header="Настройки уведомлений"
        footer="Порог Alpha — минимальное изменение полного баланса для отправки уведомления. Порог Extreme — изменение, при котором алерт считается экстремальным и запускает авто-продажу."
      >
        {settings.isLoading ? (
          <Spinner />
        ) : (
          <>
            <Input
              label="Порог Alpha (%)"
              type="number"
              value={thresholdPct}
              onChange={(e) => setThresholdPct(e.target.value)}
              placeholder="100"
            />
            <Input
              label="Порог Extreme (%)"
              type="number"
              value={extremePct}
              onChange={(e) => setExtremePct(e.target.value)}
              placeholder="1000"
            />
            {thresholdError && (
              <div style={{ padding: 'var(--space-sm) var(--space-lg)', fontSize: 'var(--font-sm)', color: 'var(--tg-destructive)' }}>
                {thresholdError}
              </div>
            )}
            <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
              <Button
                variant="primary"
                disabled={thresholdSaving}
                onClick={() => void handleThresholdSave()}
              >
                {thresholdSaving ? '…' : 'Сохранить'}
              </Button>
            </div>
          </>
        )}
      </Section>

      <Section
        header="Уведомления"
        footer="Срабатывает при изменении полного баланса (USDT + нереализованный PnL) на ≥ настроенный порог относительно одной из 20 предыдущих цен. Дубли блокируются на 10 минут; для случаев Extreme — на 60 минут."
      >
        {alerts.isLoading ? (
          <Spinner />
        ) : !alerts.data?.length ? (
          <Empty icon="🔕" title="Уведомлений пока нет" />
        ) : (
          alerts.data.map((a) => (
            <Cell
              key={a.id}
              before={a.is_extreme ? '🚨' : '⚡'}
              title={`${fmtUsd(a.prev_equity)} → ${fmtUsd(a.new_equity)}`}
              subtitle={`${fmtPct(a.change_pct)} · ${fmtDateTime(a.created_at)}`}
              after={a.is_extreme ? '>1000%' : null}
            />
          ))
        )}
      </Section>

      <Section
        header="Почасовые снимки баланса (ex-Beta)"
        footer="Каждый час в :00 сравниваем баланс с балансом часом ранее. Уведомление в Telegram приходит только если изменение превышает ±50%."
      >
        {betaAlerts.isLoading ? (
          <Spinner />
        ) : !betaAlerts.data?.length ? (
          <Empty icon="⏰" title="Пусто" hint="Первый снимок появится в ближайшие :00." />
        ) : (
          betaAlerts.data.map((a) => (
            <Cell
              key={a.id}
              before={a.notified ? (a.change_pct >= 0 ? '📈' : '📉') : '⏰'}
              title={`${fmtUsd(a.prev_equity)} → ${fmtUsd(a.new_equity)}`}
              subtitle={`${fmtPct(a.change_pct)} · ${fmtDateTime(a.new_hour)}${a.notified ? ' · уведомление отправлено' : ''}`}
            />
          ))
        )}
      </Section>
    </Page>
  );
}

/** Преобразует технический код ошибки Gate API в понятное сообщение. */
function humanizeGateError(err: string): string {
  if (/INVALID_KEY/i.test(err)) return 'Неверный API ключ. Проверьте Key в настройках.';
  if (/INVALID_SIGNATURE/i.test(err)) return 'Неверная подпись. Проверьте Secret в настройках.';
  if (/IP_FORBIDDEN/i.test(err) || /ip.+whitelist/i.test(err)) return 'IP-адрес сервера не в белом списке Gate API.';
  if (/FORBIDDEN/i.test(err) || /403/.test(err)) return 'Недостаточно прав. Убедитесь, что ключ имеет доступ к Options.';
  if (/401/.test(err)) return 'Ошибка аутентификации (401). Проверьте Key и Secret.';
  return err;
}
